import { memo } from 'react';
import { MdAddCircle } from 'react-icons/md';
import { AztecPortion } from 'types/models';
import { isPortionAvailable } from 'menu/utils';
import { useMenuGroups } from 'contexts/MenuContext';
import { PortionInfo } from 'menu/components/MenuModal/ModalBody/PortionInfo';
import { CustomFormattedNumber } from 'common/components/CustomFormattedNumber';
import { useConfig } from 'contexts/ConfigContext';
import { handleReturnKeyPress } from 'utils';

interface PortionItemProps {
  portion: AztecPortion;
  onPortionClick: (id: number) => void;
}

export const PortionItem: React.FC<PortionItemProps> = memo(
  ({ portion, onPortionClick }) => {
    const { currency, currencySymbolsDisabled } = useConfig();
    const { choiceGroups, products } = useMenuGroups();

    // Validate portion
    const portionIsAvailable = isPortionAvailable(
      portion,
      choiceGroups,
      products,
    );

    const handlePortionClick = () => {
      if (portionIsAvailable) {
        onPortionClick(portion.id);
      }
    };

    const portionDisabledClass = !portionIsAvailable ? 'disabled' : '';

    return (
      <div
        className={`portion-row ${portionDisabledClass}`}
        role="button"
        onClick={handlePortionClick}
        tabIndex={0}
        onKeyDown={(e) => handleReturnKeyPress(e, handlePortionClick)}
        aria-disabled={!portionIsAvailable}
      >
        <div className="portion-selector">
          <button
            className={`icon-button icon-button-primary ${portionDisabledClass}`}
            onClick={(e) => {
              e.stopPropagation();
              handlePortionClick();
            }}
            aria-label="Add portion"
            data-testid="portion-item-add"
            disabled={!portionIsAvailable}
          >
            <MdAddCircle size={25} />
          </button>
          <PortionInfo portion={portion} />
        </div>
        <CustomFormattedNumber
          style={currencySymbolsDisabled ? 'decimal' : 'currency'}
          value={portion.price}
          currency={currency}
          className="item-price portion-price"
          hideZeroValue
          minimumFractionDigits={2}
        />
      </div>
    );
  },
);
